import React, { useRef, useState, useEffect, forwardRef } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Checkbox,
  Chip,
  FormHelperText,
  MenuItem,
  TextField,
  ListItem,
  InputAdornment,
  IconButton
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import SearchIcon from "@mui/icons-material/Search";
import { useController } from "react-hook-form";
import { ReactComponent as Arrow } from "../../../Assets/Images/ArrowDown.svg";
import { FormItemsWrap } from "../../molecules/Wrap/Wrap";
import Label from "../Input/Label";

const defaultFilter = createFilterOptions();

export const MultipleSelectOption = ({
  control,
  label,
  name,
  getOptionLabel,
  options,
  size,
  menuItemKey,
  fullWidth,
  required,
  placeholder,
  value,
  clearIcon,
  showSelectAll = true,
  limitTags = 2,
  error = false,
  helperText = "", 
  loading = false,
  customSearch = false 
}) => {
  const { field, fieldState } = useController({
    control: control,
    name: name,
  });

  const listboxRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const isSelectingRef = useRef(false);
  const [open, setOpen] = useState(false);
  const isChipDeletingRef = useRef(false);
  const autocompleteRef = useRef(null);
  const lastClickTimeRef = useRef(0);

  const [inputValue, setInputValue] = useState(""); 
  const inputValueRef = useRef(""); 
  const [appliedSearchTerm, setAppliedSearchTerm] = useState(""); 
  const [isLocalSearching, setIsLocalSearching] = useState(false); 

  const triggerSearch = () => {
    if (!customSearch) return;
    if (!open) setOpen(true); 
    setIsLocalSearching(true); 
    
    setTimeout(() => {
      setAppliedSearchTerm(inputValueRef.current); 
      setIsLocalSearching(false); 
    }, 400); 
  };

  const customFilter = (optionsToFilter) => {
    const stripTitleForSort = (name) => {
      if (!name) return "";
      const genericTitlePattern = /^[a-zA-Z]{1,6}\.\s+/i;
      return name.replace(genericTitlePattern, '').trim().toLowerCase();
    };

    if (!appliedSearchTerm) return optionsToFilter;

    const term = appliedSearchTerm.toLowerCase().trim();
    if (!term) return optionsToFilter;

    const prefixMatches = [];
    const wordStartMatches = [];
    const partialMatches = [];

    optionsToFilter.forEach((opt) => {
      const labelStr = getOptionLabel(opt);
      if (!labelStr) return;
      const label = labelStr.toLowerCase();
      
      if (label.startsWith(term)) {
        prefixMatches.push(opt);
      } else if (label.includes(` ${term}`)) {
        wordStartMatches.push(opt);
      } else if (label.includes(term)) {
        partialMatches.push(opt);
      }
    });

    const sortAlphabetically = (a, b) => {
      const cleanA = stripTitleForSort(getOptionLabel(a));
      const cleanB = stripTitleForSort(getOptionLabel(b));
      return cleanA.localeCompare(cleanB);
    }

    return [
      ...prefixMatches.sort(sortAlphabetically),
      ...wordStartMatches.sort(sortAlphabetically),
      ...partialMatches.sort(sortAlphabetically),
    ];
  };

  const hasError = error || !!fieldState?.invalid;
  const errorMessage = helperText || fieldState?.error?.message;

  // State for responsive limit tags
  const [responsiveLimitTags, setResponsiveLimitTags] = useState(limitTags);
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState(300);

  // Calculate responsive limit tags and dropdown height based on screen size
  useEffect(() => {
    const calculateResponsiveValues = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      // Calculate limit tags based on width
      if (width >= 1536) {
        setResponsiveLimitTags(6);
      } else if (width >= 1280) {
        setResponsiveLimitTags(5);
      } else if (width >= 1024) {
        setResponsiveLimitTags(4);
      } else if (width >= 768) {
        setResponsiveLimitTags(3);
      } else if (width >= 640) {
        setResponsiveLimitTags(2);
      } else {
        setResponsiveLimitTags(1);
      }


      const safeMaxHeight = Math.min(400, height * 0.45);
      setDropdownMaxHeight(safeMaxHeight);
    };

    calculateResponsiveValues();
    window.addEventListener('resize', calculateResponsiveValues);
    return () => window.removeEventListener('resize', calculateResponsiveValues);
  }, []);

   // Handle click outside to close dropdown
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
        // Check if click is outside the autocomplete component
        const popperElement = document.querySelector('.MuiAutocomplete-popper');
        if (popperElement && !popperElement.contains(event.target)) {
          setOpen(false);
        }
      }
    };

    // Add listener with a slight delay to avoid immediate triggering
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  // Check if all options are selected
  const allSelected = value && options && value.length === options.length;
  // Check if any options are selected
  const hasSelection = value && value.length > 0;

  // Restore scroll position after value changes (only when selecting from dropdown)
  useEffect(() => {
    if (isSelectingRef.current && listboxRef.current && open) {
      requestAnimationFrame(() => {
        if (listboxRef.current && open) {
          listboxRef.current.scrollTop = scrollPositionRef.current;
        }
        setTimeout(() => {
          if (listboxRef.current && open) {
            listboxRef.current.scrollTop = scrollPositionRef.current;
            isSelectingRef.current = false;
          }
        }, 50);
      });
    } else if (!open) {
      // Reset selecting flag when dropdown closes
      isSelectingRef.current = false;
    }
  }, [value, open]);

  const handleOpen = () => {
    const now = Date.now();
    lastClickTimeRef.current = now;
    setOpen(true);
    // Restore scroll position after dropdown opens
    setTimeout(() => {
      if (listboxRef.current && scrollPositionRef.current > 0) {
        listboxRef.current.scrollTop = scrollPositionRef.current;
      }
    }, 100);
  };

  const handleClose = (event, reason) => {
      const now = Date.now();
      const timeSinceLastClick = now - lastClickTimeRef.current;
    
    // Prevent accidental closes from rapid clicking (within 200ms)
    // but allow intentional closes (arrow click, blur, escape, etc.)
    if (reason === 'toggleInput' && timeSinceLastClick < 200) {
      return;
    }
    
    lastClickTimeRef.current = now;
    // Always save scroll position before closing
    if (listboxRef.current) {
      scrollPositionRef.current = listboxRef.current.scrollTop;
    }
    
    // Allow natural closing behavior
    setOpen(false);
    
    // Reset chip deleting flag after close
    isChipDeletingRef.current = false;
  };

  const handleSelectAll = () => {
    // Save scroll position before update
    if (listboxRef.current) {
      scrollPositionRef.current = listboxRef.current.scrollTop;
      isSelectingRef.current = true;
    }
    
    field.onChange(options);
  };

  const handleDeselectAll = () => {
    // Save scroll position before update
    if (listboxRef.current) {
      scrollPositionRef.current = listboxRef.current.scrollTop;
      isSelectingRef.current = true;
    }
    
    field.onChange([]);
  };

  const handleToggleOption = (option, event) => {
    // Prevent default behavior to avoid scrolling
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Save scroll position before update
    if (listboxRef.current) {
      scrollPositionRef.current = listboxRef.current.scrollTop;
      isSelectingRef.current = true;
    }
    
    const currentValue = value || [];
    const isSelected = currentValue.some(
      (item) => item[menuItemKey] === option[menuItemKey]
    );

    if (isSelected) {
      const newValue = currentValue.filter(
        (item) => item[menuItemKey] !== option[menuItemKey]
      );
      field.onChange(newValue);
    } else {
      field.onChange([...currentValue, option]);
    }
  };

  const handleChipDelete = (index, tagValue, e) => {
    e.stopPropagation();
    
    // Mark that we're deleting a chip
    isChipDeletingRef.current = true;
    
    // Save scroll position only if dropdown is open
    if (listboxRef.current && open) {
      scrollPositionRef.current = listboxRef.current.scrollTop;
      isSelectingRef.current = true;
    }
    
    const newValue = tagValue.filter((_, i) => i !== index);
    field.onChange(newValue);
    
    // Reset the flag after a short delay to allow the change to process
    setTimeout(() => {
      isChipDeletingRef.current = false;
    }, 100);
  };

  // Custom Listbox component with forwardRef
  const CustomListbox = forwardRef((props, ref) => {
    const { children, ...other } = props;
    
    return (
      <ul 
        {...other}
        ref={(node) => {
          listboxRef.current = node;
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        style={{ 
          ...other.style, 
          maxHeight: `${dropdownMaxHeight}px`, 
          overflow: "auto",
          padding: 0,
          paddingBottom: "8px", 
          margin: 0,
        }}
      >
        {showSelectAll && options && options.length > 0 && (
          <div style={{ 
            position: "sticky", 
            top: 0, 
            backgroundColor: "#fff", 
            zIndex: 1,
            borderBottom: "1px solid #E0E0E0",
            marginBottom: 0,
          }}>
            {!allSelected && (
              <ListItem
                button
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSelectAll();
                }}
                sx={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#1976D2",
                  padding: "8px 16px",
                  cursor: "pointer",
                  margin: 0,
                  "&:hover": {
                    backgroundColor: "rgba(25, 118, 210, 0.04)",
                  },
                }}
              >
                Select All
              </ListItem>
            )}
            {hasSelection && (
              <ListItem
                button
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeselectAll();
                }}
                sx={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#1976D2",
                  padding: "8px 16px",
                  cursor: "pointer",
                  margin: 0,
                  borderTop: !allSelected ? "1px solid #E0E0E0" : "none",
                  "&:hover": {
                    backgroundColor: "rgba(25, 118, 210, 0.04)",
                  },
                }}
              >
                Deselect All
              </ListItem>
            )}
          </div>
        )}
        {children}
      </ul>
    );
  });

  CustomListbox.displayName = 'CustomListbox';

  return (
    <FormItemsWrap height={"auto"}>
      <Label label={label} required={required} />
      <div ref={autocompleteRef}>
              <Autocomplete
                  limitTags={responsiveLimitTags}
                  clearIcon={clearIcon || false}
                  fullWidth={fullWidth}
                  multiple
                  variant="outlined"
                  open={open}
                  loading={loading || isLocalSearching}
                  onOpen={handleOpen}
                  onClose={handleClose}
                  getOptionLabel={getOptionLabel}
                  filterOptions={(opts, state) => {
                    if (customSearch) {
                      return customFilter(opts);
                    } else {
                      return defaultFilter(opts, state);
                    }
                  }}
                  noOptionsText="No records found."
                  inputValue={inputValue}
                  onInputChange={(event, newInputValue, reason) => {
                    setInputValue(newInputValue);
                    inputValueRef.current = newInputValue; 

                    if (!customSearch) {
                      setAppliedSearchTerm(newInputValue);
                    } else {
                      if (!newInputValue || reason === "clear") {
                        setAppliedSearchTerm(""); 
                      }
                    }
                  }}
                  sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "pre",
                      width: "100%",
                      "& .MuiAutocomplete-popupIndicator": {
                          marginTop: "calc(54% - .5em)",
                          backgroundColor: "transparent",
                          marginRight: "11px",
                      },
                      "& .MuiTouchRipple-root": {
                          display: "none",
                      },
                      "& .MuiOutlinedInput-root": {
                          height: "auto",
                          cursor: "pointer",
                      },
                      "& .MuiChip-root": {
                          cursor: "pointer",
                      },
                      "& .MuiAutocomplete-tag": {
                          cursor: "pointer",
                      },
                  }}
                  loadingText={
                    <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  }
                  isOptionEqualToValue={(option, val) =>
                      option[menuItemKey] === val[menuItemKey]
                  }
                  disableCloseOnSelect
                  freeSolo={false}
                  popupIcon={<Arrow height={"16px"} width={"16px"} />}
                  placeholder={placeholder}
                  value={value || []}
                  onChange={(e, data, reason) => {
                      // Only save scroll position when selecting from dropdown (not when deleting chips)
                      if (reason === 'selectOption' || reason === 'removeOption') {
                          if (listboxRef.current && open && !isChipDeletingRef.current) {
                              scrollPositionRef.current = listboxRef.current.scrollTop;
                              isSelectingRef.current = true;
                          }
                      }

                      field.onChange(data);
                  }}
                  renderTags={(tagValue, getTagProps) =>
                      tagValue.map((option, index) => {
                          const tagProps = getTagProps({ index });
                          return (
                              <Chip
                                  {...tagProps}
                                  key={`${option[menuItemKey]}-${index}`}
                                  label={getOptionLabel(option)}
                                  onDelete={(e) => handleChipDelete(index, tagValue, e)}
                                  onClick={(e) => {
                                      e.stopPropagation();
                                      if (!open) {
                                          handleOpen();
                                      }
                                  }}
                                  onMouseDown={(e) => {
                                      // Don't prevent default - allow natural behavior
                                      const isDeleteIcon = e.target.closest('.MuiChip-deleteIcon');
                                      if (!isDeleteIcon && !open) {
                                          e.preventDefault();
                                      }
                                  }}
                                  sx={{
                                      backgroundColor: "#E8F4FD",
                                      color: "#37373A",
                                      fontSize: "13px",
                                      fontWeight: 400,
                                      border: "none",
                                      borderRadius: "16px",
                                      height: "32px",
                                      cursor: "pointer",
                                      "& .MuiChip-label": {
                                          paddingLeft: "12px",
                                          paddingRight: "12px",
                                          fontSize: "13px",
                                      },
                                      "& .MuiChip-deleteIcon": {
                                          color: "#1976D2",
                                          fontSize: "18px",
                                          marginRight: "4px",
                                          "&:hover": {
                                              color: "#1565C0",
                                          },
                                      },
                                      "&:hover": {
                                          backgroundColor: "#D6EBFA",
                                      },
                                  }}
                              />
                          );
                      })
                  }
                  renderOption={(props, option) => {
                      const { key, onClick, ...otherProps } = props;
                      const isSelected =
                          value &&
                          value.some((item) => item[menuItemKey] === option[menuItemKey]);

                      return (
                          <li
                              key={`${option[menuItemKey]}-option`}
                              {...otherProps}
                              onClick={(e) => {
                                  handleToggleOption(option, e);
                              }}
                              style={{
                                  fontSize: "13px",
                                  color: "#37373A",
                                  cursor: "pointer",
                                  backgroundColor: isSelected ? "#E8F4FD" : "transparent",
                                  fontWeight: isSelected ? 500 : 400,
                              }}
                          >
                              {getOptionLabel(option)}
                          </li>
                      );
                  }}
                  ListboxProps={{
                      sx: {
                          "& .MuiAutocomplete-option": {
                              padding: "6px 16px",
                              "&:hover": {
                                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                              },
                          },
                          maxHeight: `${dropdownMaxHeight}px`,
                          overflow: "auto",
                          padding: 0,
                          margin: 0,
                      },
                  }}
                  componentsProps={{
                      paper: {
                          sx: {
                              maxHeight: `${dropdownMaxHeight + 50}px`,
                              borderRadius: "4px",
                              marginTop: "4px",
                              overflow: "hidden", // Prevent content from showing outside
                          },
                      },
                      popper: {
                          placement: "bottom-start",
                          modifiers: [
                              {
                                  name: "flip",
                                  enabled: true,
                                  options: {
                                      boundary: 'window', 
                                      fallbackPlacements: ['top-start']
                                  }
                              },
                              {
                                  name: "preventOverflow",
                                  enabled: true,
                                  options: {
                                      boundary: 'window',
                                      altAxis: true,
                                      padding: 15 
                                  },
                              },
                          ],
                      },
                  }}
                  renderInput={(params) => (
                      <TextField
                          {...params}
                          placeholder={placeholder}
                          error={!!fieldState?.invalid}
                          onKeyDown={(e) => {
                            if (customSearch && e.key === "Enter") {
                              e.preventDefault();
                              e.stopPropagation();
                              triggerSearch();
                            }
                          }}
                          onMouseDown={(e) => {
                              const target = e.target;
                              const isButton = target.closest('button');
                              const isArrow = target.closest('.MuiAutocomplete-popupIndicator');
                              const isDeleteIcon = target.closest('.MuiChip-deleteIcon');
                              // Don't open if clicking delete icon
                              if (!isButton && !isArrow && !isDeleteIcon && !open) {
                                  e.preventDefault();
                                  handleOpen();
                              }
                          }}
                          onFocus={() => {
                              // Don't open if we just deleted a chip
                              if (!open && !isChipDeletingRef.current) {
                                  handleOpen();
                              }
                          }}
                          InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                  <React.Fragment>
                                      {params.InputProps.endAdornment}
                                      {customSearch && (
                                        <InputAdornment position="end" sx={{ position: "absolute", right: "50px" }}>
                                          <IconButton 
                                            onMouseDown={(e) => {
                                              e.preventDefault(); 
                                              e.stopPropagation();
                                            }} 
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              triggerSearch();
                                            }} 
                                            edge="end" 
                                            size="medium"
                                          >
                                            <SearchIcon fontSize="medium" />
                                          </IconButton>
                                        </InputAdornment>
                                      )}
                                  </React.Fragment>
                              ),
                          }}
                      />
                  )}
                  ListboxComponent={CustomListbox}
                  options={options}
              />
      </div>
      {hasError && errorMessage && (
        <FormHelperText 
          sx={{
            color: "#d32f2f",
            fontSize: "0.75rem",
            marginTop: "4px",
            marginLeft: "14px",
            marginRight: "14px",
          }}
        >
          {errorMessage}
        </FormHelperText>
      )}
    </FormItemsWrap>
  );
};